'use client'

import { Button } from '@/components/elements/Button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { X, Minimize2, Maximize2, User, Loader2, AlertCircle, MessageCircle, ArrowUp, ShoppingCart } from 'lucide-react'
import { InputGroup, InputGroupTextarea, InputGroupAddon, InputGroupButton, InputGroupText } from '@/components/ui/input-group'
import { Separator } from '@/components/ui/separator'
import { Card } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useIsMobile } from '@/hooks/use-mobile'
import { useScrollVisibility } from '@/hooks/use-scroll-visibility'
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel'
import moment from 'moment'
import type { CustomerChatMessage } from '@/types/customerChat'
import { usePathname } from 'next/navigation'
import { useCustomerChat } from '@/lib/commerce/hooks'
import { formatImageURL, formatPrice } from '@/utils/util'
import {
  CHAT_MAX_MESSAGE_LENGTH,
  CHAT_SUGGESTED_QUESTIONS,
  CHAT_PAYMENT_LABELS,
  parseChatMessageContent,
  getChatAlternativePayments,
  getChatProductDisplay,
} from '@/utils/chat'
import Logo from '@/assets/favicon.png'
import Image from 'next/image'

interface CustomerChatProps {
  vendor: any
  botName?: string
  botAvatar?: string
}

export default function CustomerChat({ vendor, botName, botAvatar }: CustomerChatProps) {
  const isMobile = useIsMobile()
  const pathname = usePathname()
  const isNavVisible = useScrollVisibility()
  const c = useCustomerChat({ vendor, defaultBotName: botName, defaultBotAvatar: botAvatar })

  useBodyScrollLock(!!isMobile && c.isChatOpen)

  const isProductDetailPage = /\/product\/[^/]+/.test(pathname || '')
  const mobileBottomClass = isProductDetailPage ? 'bottom-36' : 'bottom-20'
  const positionClasses = c.chatPosition === 'bottom-left'
    ? `${mobileBottomClass} left-4 lg:bottom-6 lg:left-6`
    : `${mobileBottomClass} right-4 lg:bottom-6 lg:right-6`
  const ink = '#09090b'

  const renderProductList = (products: any[]) => (
    <div className="relative w-full max-w-67.5">
      <Carousel opts={{ align: "start", loop: false }} className="w-full">
        <CarouselContent>
          {products.map((product) => {
            const { hasMultipleVariants, selectedVariantId, selectedVariant, displayHasDiscount, displayImage, displayStock, displayName, isTrackable, isInStock } = getChatProductDisplay(product, c.selectedVariants[product.id])

            return (
              <CarouselItem key={product.id} className="basis-full">
                <div className="border border-border rounded-lg overflow-hidden bg-card hover:shadow-md transition-shadow h-full">
                  <div className="relative w-full aspect-square bg-muted">
                    <img src={formatImageURL(displayImage)} alt={product.name} className="w-full h-full object-cover" />
                    {displayHasDiscount && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-md text-xs font-bold">-{product.discountPercent}%</div>
                    )}
                  </div>
                  <div className="p-3 space-y-2.5">
                    <h4 className="font-semibold text-base text-foreground line-clamp-2 leading-5">{product.name}{product.isDigital && <span className="ml-1 text-xs font-normal text-blue-600">📩 Дижитал</span>}</h4>
                    {hasMultipleVariants ? (
                      <div className="mb-3">
                        <div className="text-xs text-muted-foreground mb-1">Боломжит төрлүүд</div>
                        <Select value={selectedVariantId} onValueChange={(value) => c.handleVariantChange(product.id, value)}>
                          <SelectTrigger size="sm" className="w-full text-sm h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {product.variants.map((variant: any) => (
                              <SelectItem key={variant.id} value={variant.id} className="text-sm">
                                <div className='flex items-center gap-1'>
                                  <span>{variant.name}</span>
                                  {isTrackable && variant.stock != null && variant.stock > 0 && (
                                    <span className="text-xs text-muted-foreground">({variant.stock} ш.)</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{displayName}</span>
                        {isTrackable && displayStock != null && (
                          <span className={`font-medium ${isInStock ? 'text-green-600' : 'text-red-600'}`}>
                            {isInStock ? `${displayStock} ширхэг` : 'Дууссан'}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`font-bold text-base ${displayHasDiscount ? 'text-red-600' : 'text-foreground'}`}>{formatPrice(selectedVariant?.price)}</span>
                        {displayHasDiscount && (
                          <span className="text-xs text-muted-foreground line-through">{formatPrice(selectedVariant?.originalPrice)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => c.goToProduct(product.customId)}>Дэлгэрэнгүй</Button>
                      <Button size="sm" className="flex-1 h-8 text-xs" disabled={!isInStock || c.loading} color={ink} icon={ShoppingCart} onClick={() => c.handleAddToCart(product, selectedVariantId)}>Сагслах</Button>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            )
          })}
        </CarouselContent>
        {products.length > 1 && (
          <>
            <CarouselPrevious className="left-2 top-1/2 -translate-y-1/2" />
            <CarouselNext className="right-2 top-1/2 -translate-y-1/2" />
          </>
        )}
      </Carousel>
    </div>
  )

  const renderOrder = (metadata: any) => {
    const alternativePayments = getChatAlternativePayments(metadata, c.configMethods)
    return (
      <div className="space-y-3">
        <div className="text-sm space-y-1">
          <div>📋 Дугаар: <span className="font-semibold">{metadata.customId}</span></div>
          <div>💰 Дүн: <span className="font-semibold">{formatPrice(metadata.amount)}</span></div>
        </div>
        <Button size="sm" className="w-full" color={ink} onClick={() => c.goToOrder(metadata.customId, metadata.phone)}>
          {CHAT_PAYMENT_LABELS[metadata.paymentMethod] || '📦 Захиалга харах'}
        </Button>
        {alternativePayments.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground">Төлбөрийн хэлбэр солих:</div>
            <div className="flex flex-wrap gap-1">
              {alternativePayments.map(p => (
                <button key={p.key} onClick={() => c.handleSuggestedQuestion(p.chatLabel)} className="cursor-pointer! py-2 px-3 text-left text-xs border rounded-lg hover:bg-muted transition-colors">
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderCart = (metadata: any) => {
    const { items, dealItems, subTotal } = metadata
    return (
      <div className="space-y-2">
        <ul className="space-y-1.5">
          {items.map((item: any) => (
            <li key={item.id} className="text-sm">
              {item.name}{item.isDigital && <span className="text-blue-600"> 📩</span>} x{item.quantity} - {formatPrice(item.total)}
            </li>
          ))}
        </ul>
        {dealItems && dealItems.length > 0 && (
          <div className="text-xs text-muted-foreground">🎉 Таны сагсанд {dealItems.length} хямдралтай бараа автоматаар нэмэгдсэн байна!</div>
        )}
        <div className="font-bold pt-2 border-t border-border">Нийт: {formatPrice(subTotal)}</div>
      </div>
    )
  }

  const renderOrderTracking = (metadata: any) => (
    <Button size="sm" className="w-full" color={ink} onClick={() => c.goToOrder(metadata.customId, metadata.phone)}>
      📦 Захиалга харах
    </Button>
  )

  const renderMessage = (msg: CustomerChatMessage) => {
    const isUser = msg.senderRole === 'user'
    const isSystem = msg.senderRole === 'system'

    if (isSystem) {
      return (
        <div key={msg.id} className="flex justify-center my-2">
          <div className="bg-muted px-3 py-1.5 rounded-full text-xs text-muted-foreground">
            {msg.message}
          </div>
        </div>
      )
    }

    const parsedContent = !isUser ? parseChatMessageContent(msg.message) : null

    return (
      <div key={msg.id} className={`flex gap-2 mb-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        <Avatar className="h-8 w-8 shrink-0">
          {isUser ? (
            <AvatarFallback>
              <User className="h-4 w-4" />
            </AvatarFallback>
          ) : c.displayBotAvatar ? (
            <AvatarImage src={formatImageURL(c.displayBotAvatar)} alt="Bot Avatar" className="object-cover" />
          ) : (
            <AvatarFallback className="bg-background border border-border">
              <Image src={Logo} alt="Bot Avatar" className="h-4 w-4" />
            </AvatarFallback>
          )}
        </Avatar>
        <div className={`flex flex-col ${isUser ? 'max-w-[85%] items-end' : 'max-w-[90%] items-start'}`}>
          <div className={`rounded-lg ${isUser ? 'px-3 py-2 bg-foreground text-background' : parsedContent ? 'p-3 bg-muted text-foreground' : 'px-3 py-2 bg-muted text-foreground'}`}>
            {parsedContent ? (
              <div className="space-y-2">
                {parsedContent.type !== 'order' && <div className="text-sm" dangerouslySetInnerHTML={{ __html: parsedContent.message || '' }} />}
                {parsedContent.type === 'product_list' && renderProductList(parsedContent.metadata.products)}
                {parsedContent.type === 'cart' && renderCart(parsedContent.metadata)}
                {parsedContent.type === 'order' && renderOrder(parsedContent.metadata)}
                {parsedContent.type === 'order_tracking' && renderOrderTracking(parsedContent.metadata)}
              </div>
            ) : (
              <div className="text-sm wrap-anywhere whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: msg.message }} />
            )}
          </div>
          <span className="text-xs text-muted-foreground mt-1">
            {moment(msg.sentAt).format('HH:mm')}
          </span>
        </div>
      </div>
    )
  }

  if (!c.isMounted) return null

  if (!c.isChatOpen) {
    return (
      <button onClick={() => c.setIsChatOpen(true)} aria-label="Чат нээх" className={`fixed cursor-pointer ${positionClasses} z-50 h-14 w-14 rounded-full bg-foreground text-background shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group ${!isNavVisible ? 'translate-y-16 lg:translate-y-0' : 'translate-y-0'}`}>
        <MessageCircle className="size-6" strokeWidth={1.5} />
        {!c.isConnected && (
          <span className="absolute top-0 right-0 h-3 w-3 bg-red-500 rounded-full border-2 border-white"></span>
        )}
      </button>
    )
  }

  return (
    <Card className={`fixed z-50 flex flex-col !py-0 transition-all duration-300 !gap-0 ${isMobile ? 'top-0 left-0 right-0 w-full h-dvh rounded-none' : c.isChatExpanded ? `top-2 ${c.chatPosition === 'bottom-left' ? 'left-2' : 'right-2'} h-[calc(100vh-1rem)] w-100 rounded-lg border shadow-sm` : `${positionClasses} w-95 h-150 rounded-lg shadow-2xl border-none!`}`}>
      <div className={`flex items-center justify-between border-b transition-all duration-300 ${isMobile ? 'rounded-none' : 'rounded-t-lg'} ${isMobile || !c.isChatExpanded ? 'bg-foreground text-background p-3' : 'bg-back p-4'}`}>
        <div className="flex items-center gap-3">
          {c.displayBotAvatar ? (
            <Avatar className="h-10 w-10">
              <AvatarImage src={formatImageURL(c.displayBotAvatar)} alt="Bot Avatar" className="object-cover" />
            </Avatar>
          ) : (
            <div className='h-10 w-10 rounded-full flex items-center justify-center transition-all duration-300 bg-background'>
              <Image src={Logo} alt="Bot Avatar" className="h-5 w-5" />
            </div>
          )}
          <div>
            <h3 className={`font-semibold transition-all duration-300 ${isMobile || !c.isChatExpanded ? '' : 'text-foreground'}`}>
              {c.displayBotName}
            </h3>
            <p className={`text-xs transition-all duration-300 flex items-center gap-1.5 ${isMobile || !c.isChatExpanded ? 'opacity-90' : 'text-muted-foreground'}`}>
              {c.isConnected && (
                <span className="relative flex size-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex size-2 rounded-full bg-green-500" />
                </span>
              )}
              {c.isConnected ? 'Онлайн' : 'Холбогдож байна...'}
            </p>
          </div>
        </div>
        <div className="flex items-center">
          {!isMobile && (
            <Button size="icon" variant="ghost" onClick={() => c.setIsChatExpanded(!c.isChatExpanded)} className={`size-7 transition-all duration-300 ${c.isChatExpanded ? 'text-foreground hover:bg-muted' : 'text-white hover:bg-white/20'}`}>
              {c.isChatExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          )}
          <Button size="icon" variant="ghost" onClick={c.handleClose} className={`size-7 transition-all duration-300 ${isMobile || !c.isChatExpanded ? 'text-white hover:bg-white/20' : 'text-foreground hover:bg-muted'}`}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto transition-all duration-300 p-4`}>
        {!c.currentSession && c.isLoading ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-foreground" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">Чат эхлүүлж байна...</p>
          </div>
        ) : !c.isConnected ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <AlertCircle className="h-8 w-8 text-danger" />
            <p className="text-sm text-danger text-center font-medium">Холбогдож чадсангүй</p>
            <p className="text-xs text-muted-foreground text-center px-4">
              Интернэт холболтоо шалгаад дахин оролдоно уу
            </p>
            {c.error && (
              <p className="text-xs text-muted-foreground text-center px-4 bg-muted p-2 rounded">
                {c.error}
              </p>
            )}
          </div>
        ) : c.error ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <AlertCircle className="h-8 w-8 text-danger" />
            <p className="text-sm text-danger text-center">{c.error}</p>
            <Button onClick={c.handleStartChat} size="sm" color={ink}>
              Дахин оролдох
            </Button>
          </div>
        ) : c.messages.length <= 1 ? (
          <div className="flex flex-col h-full">
            <div className="space-y-1 flex-1">
              {c.messages.map((msg) => renderMessage(msg))}
            </div>
            <div className="flex flex-wrap gap-1 pt-4">
              {CHAT_SUGGESTED_QUESTIONS.map((question, index) => (
                <button key={index} onClick={() => c.handleSuggestedQuestion(question)} className="cursor-pointer! py-2 px-3 text-left text-xs border rounded-lg hover:bg-muted transition-colors" disabled={!c.currentSession || c.isLoading}>
                  {question}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {c.messages.map((msg) => renderMessage(msg))}
            {c.isAiTyping && (
              <div className="flex gap-2 mb-3">
                <Avatar className="h-8 w-8 shrink-0">
                  {c.displayBotAvatar ? (
                    <AvatarImage src={formatImageURL(c.displayBotAvatar)} alt="Bot Avatar" className="object-cover" />
                  ) : (
                    <AvatarFallback className="bg-background border border-border">
                      <Image src={Logo} alt="Bot Avatar" className="h-4 w-4" />
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="bg-muted px-3 py-2 rounded-lg">
                  <div className="flex gap-1 mt-1">
                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={c.messagesEndRef} />
          </div>
        )}
      </div>

      <form onSubmit={c.handleSendMessage} className={`border-t transition-all duration-300 ${isMobile ? 'py-3 px-2' : c.isChatExpanded ? 'py-3 px-2' : 'p-4'}`}>
        <div className="rounded-lg border border-border transition-colors duration-150 focus-within:border-foreground">
          <div className="bg-card rounded-lg">
            <InputGroup className="bg-transparent! rounded-lg border-0! relative z-10 focus-within:ring-0! focus-within:outline-none! has-[[data-slot=input-group-control]:focus-visible]:ring-0!">
              <InputGroupTextarea
                placeholder="Асуултаа бичнэ үү..."
                value={c.inputMessage}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => c.handleInputChange(e.target.value)}
                disabled={!c.currentSession || c.isLoading}
                rows={c.textareaRows}
                className="focus:ring-0! focus-visible:ring-0! focus:border-none! focus-visible:border-none! focus:outline-none! focus-visible:outline-none! pt-3! pb-2! min-h-0! max-h-18!"
                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    c.handleSendMessage(e)
                  }
                }}
              />
              <InputGroupAddon align="block-end">
                <InputGroupText className={`text-xs ${c.inputMessage.length > CHAT_MAX_MESSAGE_LENGTH ? 'text-danger' : 'text-muted-foreground'}`}>
                  {c.inputMessage.length}/{CHAT_MAX_MESSAGE_LENGTH}
                </InputGroupText>
                <Separator orientation="vertical" className="h-4! ml-auto" />
                <InputGroupButton
                  variant="default"
                  className="rounded-full bg-foreground hover:bg-foreground/85"
                  size="icon-xs"
                  type="submit"
                  disabled={!c.inputMessage.trim() || !c.currentSession || c.isLoading || c.inputMessage.length > CHAT_MAX_MESSAGE_LENGTH}
                >
                  <ArrowUp className="text-background" />
                  <span className="sr-only">Send</span>
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
          </div>
        </div>
      </form>
    </Card>
  )
}
